import { canUseShowPricesByGroupForRoute } from 'utils/showPricesByGroup';
import growthBook from 'services/growthBook';
import { deriveSeatPriceZonesFromLayout, getSeatPriceZonesFromSource } from 'utils/seatPricing';

/**
 * Origin and destination display names from trip (or first fragment fallback).
 * @param {object} [trip] - Trip with origin/destination or fragments.
 * @returns {{ origin: string, destination: string }}
 */
const getTripPlaceNames = (trip) => {
  const firstFragment = trip?.fragments?.[0];
  const origin = trip?.origin?.cityName ?? trip?.origin?.name ?? firstFragment?.origin?.cityName;
  const destination =
    trip?.destination?.cityName ?? trip?.destination?.name ?? firstFragment?.destination?.cityName;
  return { origin, destination };
};

/**
 * Human-readable route label for seat analytics (e.g. "City A - City B").
 * @param {object} params - routeLabel, trip.
 * @param {string} [params.routeLabel] - Explicit label when already computed.
 * @param {object} [params.trip] - Trip with origin/destination or fragments.
 * @returns {string} Label or empty string when names are missing.
 */
export const getSeatSelectorDepartureRoute = ({ routeLabel, trip }) => {
  if (routeLabel) return routeLabel;
  const { origin, destination } = getTripPlaceNames(trip);
  if (!origin || !destination) return '';
  return `${origin} - ${destination}`;
};

/**
 * Resolves seat price zones for SeatSelector from its props (incl. GrowthBook flag).
 * @param {object} props - SeatSelector props subset.
 * @param {object} props.features - Whitelabel features (`USE_SEAT_BASED_PRICING`).
 * @param {Array<object>} [props.seatPriceZones] - Zones from parent when present.
 * @param {Array} [props.layout] - Bus layout for layout-derived zones fallback.
 * @param {object} [props.trip] - Trip for source zones and route gating.
 * @param {object} [props.showPricesByGroupConfig] - Route allowlist config.
 * @returns {Array<object>} Zones to apply, or empty when gating disables seat groups.
 */
export const getResolvedSeatPriceZonesForSeatSelector = (props) => {
  const { features, seatPriceZones, layout, trip, showPricesByGroupConfig } = props;
  const fallbackSeatPriceZones = deriveSeatPriceZonesFromLayout(layout);
  let candidateSeatPriceZones;
  if (Array.isArray(seatPriceZones) && seatPriceZones.length > 0) {
    candidateSeatPriceZones = seatPriceZones;
  } else if (Array.isArray(fallbackSeatPriceZones) && fallbackSeatPriceZones.length > 0) {
    candidateSeatPriceZones = fallbackSeatPriceZones;
  } else {
    candidateSeatPriceZones = getSeatPriceZonesFromSource(trip);
  }
  const showPricesByGroupGB = Boolean(growthBook.getGrowthBookFeature('show_prices_by_group'));
  const { origin, destination } = getTripPlaceNames(trip);
  const isRouteEnabled = canUseShowPricesByGroupForRoute({
    origin,
    destination,
    showPricesByGroupConfig,
  });
  const shouldApplySeatZones =
    features.USE_SEAT_BASED_PRICING && showPricesByGroupGB && isRouteEnabled;
  return shouldApplySeatZones ? candidateSeatPriceZones : [];
};
